import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import {
  useTransactions,
  useCategories,
  useSubscription,
  useRecurring,
  DEFAULT_EXPENSE_CATEGORIES,
  DEFAULT_INCOME_CATEGORIES,
  Category,
  sameMonth,
  PLAN_LIMITS,
} from "@/lib/store";
import { useAuthCtx } from "@/lib/auth-context";
import { useMemo, useState } from "react";
import { ArrowDownLeft, ArrowUpRight, Loader2, Plus, Sparkles, X, Lock, Repeat } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { AdicionarSkeleton } from "@/components/skeletons";

export const Route = createFileRoute("/_app/adicionar")({
  head: () => ({
    meta: [
      { title: "Adicionar transação — Seu Bolso Tranquilo" },
      { name: "description", content: "Registre uma nova receita ou despesa em segundos: valor, categoria, data e descrição com categorias personalizadas." },
      { property: "og:title", content: "Adicionar transação — Seu Bolso Tranquilo" },
      { property: "og:description", content: "Registre receitas e despesas rapidamente, com categorias personalizadas." },
      { name: "robots", content: "noindex, follow" },
    ],
    links: [{ rel: "canonical", href: "https://seubolsotranquilo.com/adicionar" }],
  }),
  component: AdicionarPage,
  pendingComponent: AdicionarSkeleton,
});

function AdicionarPage() {
  const { user } = useAuthCtx();
  const { transactions, addTransaction } = useTransactions(user?.id);
  const { categories: customCategories, addCategory } = useCategories(user?.id);
  const { isPremium } = useSubscription(user?.id);
  const { addRule } = useRecurring(user?.id);
  const navigate = useNavigate();
  const [type, setType] = useState<"income" | "expense">("expense");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState<Category>(DEFAULT_EXPENSE_CATEGORIES[0]);
  const [description, setDescription] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [showNewCat, setShowNewCat] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [creatingCat, setCreatingCat] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  const [frequency, setFrequency] = useState<"daily" | "weekly" | "monthly" | "yearly">("monthly");

  const monthlyLimit = PLAN_LIMITS.free.transactionsPerMonth;
  const usedThisMonth = useMemo(
    () => transactions.filter((t) => sameMonth(t.date)).length,
    [transactions]
  );
  const usagePct = Math.min(100, Math.round((usedThisMonth / monthlyLimit) * 100));
  const nearLimit = !isPremium && usedThisMonth >= monthlyLimit - 5;
  const atLimit = !isPremium && usedThisMonth >= monthlyLimit;

  const allCategories = useMemo(() => {
    const defaultForType =
      type === "income" ? DEFAULT_INCOME_CATEGORIES : DEFAULT_EXPENSE_CATEGORIES;
    const customForType = customCategories
      .filter((c) => c.type === type)
      .map((c) => c.name);
    // Dedupe while preserving defaults order, then append custom ones
    const seen = new Set<string>();
    const merged: string[] = [];
    for (const c of [...defaultForType, ...customForType]) {
      if (!seen.has(c)) {
        seen.add(c);
        merged.push(c);
      }
    }
    return merged;
  }, [customCategories, type]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = parseFloat(amount.replace(",", "."));
    if (!value || value <= 0) {
      toast.error("Valor inválido.");
      return;
    }
    setSaving(true);
    // Parse the YYYY-MM-DD as a LOCAL date at noon so the resulting ISO
    // string falls on the same calendar day in the user's timezone (avoids
    // the UTC-midnight off-by-one bug where a date entered as "2026-04-21"
    // would appear as 2026-04-20 in negative-offset timezones like BRT).
    const [yyyy, mm, dd] = date.split("-").map(Number);
    const localDate = new Date(yyyy, (mm ?? 1) - 1, dd ?? 1, 12, 0, 0, 0);
    const res = await addTransaction({
      type,
      amount: value,
      category,
      description: description || category,
      date: localDate.toISOString(),
    });
    setSaving(false);
    if (res?.error) {
      if (import.meta.env.DEV) {
        console.error("Transaction save error:", res.error);
      }
      const msg = res.error.message ?? "";
      if (msg.includes("TRANSACTIONS_MONTHLY_LIMIT_REACHED")) {
        toast.error(
          "Você atingiu o limite de 30 transações deste mês. Faça upgrade para o Premium para registrar transações ilimitadas."
        );
      } else {
        toast.error("Não foi possível salvar a transação. Tente novamente.");
      }
      return;
    }
    if (isRecurring) {
      const r = await addRule({
        type, amount: value, category,
        description: description || category,
        frequency, start_date: date,
      });
      if (r?.error) {
        toast.success("Transação salva, mas a recorrência não pôde ser criada.");
      } else {
        toast.success("Transação salva e recorrência ativada!");
      }
    } else {
      toast.success("Transação salva!");
    }
    navigate({ to: "/dashboard" });
  };

  const handleCreateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = newCatName.trim();
    if (!name) {
      toast.error("Digite um nome para a categoria.");
      return;
    }
    if (name.length > 30) {
      toast.error("Use no máximo 30 caracteres.");
      return;
    }
    setCreatingCat(true);
    const res = await addCategory({ name, type });
    setCreatingCat(false);
    if (res?.error) {
      if (import.meta.env.DEV) console.error("Category create error:", res.error);
      const msg = res.error.message ?? "";
      if (msg.includes("CATEGORIES_LIMIT_REACHED")) {
        toast.error(
          "Você atingiu o limite de 2 categorias do plano gratuito. Faça upgrade para o Premium para criar categorias ilimitadas."
        );
      } else if (msg.includes("duplicate") || msg.includes("unique")) {
        toast.error("Você já tem uma categoria com esse nome.");
      } else {
        toast.error("Não foi possível criar a categoria. Tente novamente.");
      }
      return;
    }
    toast.success("Categoria criada!");
    setCategory(name);
    setNewCatName("");
    setShowNewCat(false);
  };

  return (
    <div className="p-6 lg:p-10 max-w-2xl mx-auto space-y-6">
      <header>
        <h1 className="font-display text-3xl lg:text-4xl font-bold">Nova transação</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Registre uma receita ou despesa em segundos.
        </p>
      </header>

      {!isPremium && (
        <div
          className={cn(
            "rounded-2xl border p-4 space-y-2.5 transition-colors",
            atLimit
              ? "border-destructive/40 bg-destructive/10"
              : nearLimit
                ? "border-warning/40 bg-warning/10"
                : "border-border/40 bg-gradient-card"
          )}
        >
          <div className="flex items-center justify-between gap-3 text-sm">
            <span className="font-medium">
              {usedThisMonth} de {monthlyLimit} transações usadas neste mês
            </span>
            <Link
              to="/"
              hash="precos"
              className="inline-flex items-center gap-1 text-xs font-semibold text-primary-glow hover:underline"
            >
              <Sparkles className="size-3" /> Upgrade
            </Link>
          </div>
          <div className="h-2 w-full rounded-full bg-background/60 overflow-hidden">
            <div
              className={cn(
                "h-full rounded-full transition-all",
                atLimit
                  ? "bg-destructive"
                  : nearLimit
                    ? "bg-warning"
                    : "bg-gradient-primary"
              )}
              style={{ width: `${usagePct}%` }}
            />
          </div>
          {atLimit && (
            <p className="text-xs text-destructive">
              Você atingiu o limite do plano gratuito. Faça upgrade para registrar transações ilimitadas.
            </p>
          )}
        </div>
      )}

      <form
        onSubmit={handleSubmit}
        className="rounded-3xl bg-gradient-card border border-border/40 p-6 space-y-5"
      >
        <div className="grid grid-cols-2 gap-2 p-1.5 rounded-2xl bg-background/40 border border-border/40">
          <button
            type="button"
            onClick={() => {
              setType("expense");
              setCategory(DEFAULT_EXPENSE_CATEGORIES[0]);
            }}
            className={cn(
              "flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-all",
              type === "expense"
                ? "bg-destructive/15 text-destructive shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <ArrowUpRight className="size-4" /> Despesa
          </button>
          <button
            type="button"
            onClick={() => {
              setType("income");
              setCategory(DEFAULT_INCOME_CATEGORIES[0]);
            }}
            className={cn(
              "flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium transition-all",
              type === "income"
                ? "bg-success/15 text-success shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <ArrowDownLeft className="size-4" /> Receita
          </button>
        </div>

        <div className="space-y-2">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Valor
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground font-display">
              R$
            </span>
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0,00"
              required
              className="w-full pl-12 pr-4 py-4 rounded-2xl bg-input border border-border/40 text-2xl font-display font-bold focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
            />
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Categoria
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {allCategories.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCategory(c)}
                className={cn(
                  "px-3 py-2.5 rounded-xl text-sm font-medium border transition-all",
                  category === c
                    ? "bg-primary/15 border-primary/50 text-primary-glow"
                    : "border-border/40 text-muted-foreground hover:bg-accent/40"
                )}
              >
                {c}
              </button>
            ))}
            <button
              type="button"
              onClick={() => {
                if (!isPremium) {
                  toast.error(
                    "Categorias personalizadas são exclusivas do plano Premium."
                  );
                  return;
                }
                setShowNewCat(true);
              }}
              className={cn(
                "px-3 py-2.5 rounded-xl text-sm font-medium border border-dashed transition-all inline-flex items-center justify-center gap-1.5",
                isPremium
                  ? "border-primary/40 text-primary-glow hover:bg-primary/10"
                  : "border-border/40 text-muted-foreground hover:bg-accent/40"
              )}
              title={isPremium ? "Criar nova categoria" : "Recurso Premium"}
            >
              {isPremium ? <Plus className="size-4" /> : <Lock className="size-3.5" />}
              Nova categoria
            </button>
          </div>
          {!isPremium && (
            <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
              <Sparkles className="size-3 text-primary-glow" />
              <Link to="/" className="text-primary-glow hover:underline">
                Faça upgrade para o Premium
              </Link>{" "}
              para criar categorias personalizadas.
            </p>
          )}
        </div>

        <div className="space-y-2">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Descrição
          </label>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Ex: Almoço com amigos"
            className="w-full px-4 py-3 rounded-2xl bg-input border border-border/40 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
          />
        </div>

        <div className="space-y-2">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Data
          </label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full px-4 py-3 rounded-2xl bg-input border border-border/40 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
          />
        </div>

        <div className="space-y-3">
          <button
            type="button"
            onClick={() => {
              if (!isPremium && !isRecurring) {
                toast.error("Transações recorrentes são exclusivas do plano Premium.");
                return;
              }
              setIsRecurring((v) => !v);
            }}
            className={cn(
              "w-full flex items-center gap-3 rounded-2xl border p-4 text-left transition-all",
              isRecurring
                ? "border-primary/50 bg-primary/10 shadow-glow"
                : "border-border/40 bg-background/40 hover:bg-accent/30"
            )}
          >
            <div
              className={cn(
                "flex items-center justify-center rounded-xl size-10 transition-colors",
                isRecurring ? "bg-primary/20 text-primary-glow" : "bg-muted text-muted-foreground"
              )}
            >
              <Repeat className="size-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium inline-flex items-center gap-2">
                Repetir transação automaticamente
                {!isPremium && <Lock className="size-3 text-muted-foreground" />}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                Ideal para aluguel, salário, assinaturas e outros lançamentos fixos.
              </p>
            </div>
            <div
              className={cn(
                "relative h-6 w-11 rounded-full transition-colors",
                isRecurring ? "bg-primary" : "bg-muted"
              )}
            >
              <span
                className={cn(
                  "absolute top-1 left-1 h-4 w-4 rounded-full bg-white transition-transform",
                  isRecurring ? "translate-x-5" : "translate-x-0"
                )}
              />
            </div>
          </button>

          {isRecurring && (
            <div className="space-y-2 pl-2">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Frequência
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {([
                  { v: "daily", label: "Diária" },
                  { v: "weekly", label: "Semanal" },
                  { v: "monthly", label: "Mensal" },
                  { v: "yearly", label: "Anual" },
                ] as const).map((f) => (
                  <button
                    key={f.v}
                    type="button"
                    onClick={() => setFrequency(f.v)}
                    className={cn(
                      "px-3 py-2.5 rounded-xl text-sm font-medium border transition-all",
                      frequency === f.v
                        ? "bg-primary/15 border-primary/50 text-primary-glow shadow-glow"
                        : "border-border/40 text-muted-foreground hover:bg-accent/40"
                    )}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <button
          type="submit"
          disabled={saving}
          className="w-full py-3.5 rounded-2xl bg-gradient-primary text-primary-foreground font-semibold shadow-glow hover:opacity-90 transition-opacity disabled:opacity-60 inline-flex items-center justify-center gap-2"
        >
          {saving && <Loader2 className="size-4 animate-spin" />}
          Salvar transação
        </button>
      </form>

      {showNewCat && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/70 backdrop-blur-sm"
          onClick={() => setShowNewCat(false)}
        >
          <div
            className="w-full max-w-sm rounded-3xl bg-gradient-card border border-border/40 p-6 shadow-glow"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Sparkles className="size-4 text-primary-glow" />
                <h2 className="font-display text-lg font-semibold">Nova categoria</h2>
              </div>
              <button
                type="button"
                onClick={() => setShowNewCat(false)}
                className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/40"
                aria-label="Fechar"
              >
                <X className="size-4" />
              </button>
            </div>
            <form onSubmit={handleCreateCategory} className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">
                  Tipo: {type === "expense" ? "Despesa" : "Receita"}
                </label>
                <input
                  autoFocus
                  value={newCatName}
                  onChange={(e) => setNewCatName(e.target.value)}
                  placeholder="Ex: Pet, Educação, Investimentos…"
                  maxLength={30}
                  className="w-full px-4 py-3 rounded-2xl bg-input border border-border/40 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all"
                />
              </div>
              <button
                type="submit"
                disabled={creatingCat}
                className="w-full py-3 rounded-2xl bg-gradient-primary text-primary-foreground font-semibold shadow-glow hover:opacity-90 transition-opacity disabled:opacity-60 inline-flex items-center justify-center gap-2"
              >
                {creatingCat && <Loader2 className="size-4 animate-spin" />}
                Criar categoria
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
